import React, { useState } from 'react';
import {
  Button,
  CardActionArea,
  CardActionAreaProps,
  Grid,
  GridProps,
  Snackbar,
  SnackbarProps,
  Stack,
  SxProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import { getSubTopicState } from '../../../../store/SubTopicSlice';
import AddResourceFeedback from '../../../../store/V2/ResourceFeedbackSlice/api';
import { getQuestionState } from '../../../../store/QuestionSlice';

const stackStyles: SxProps = {
  padding: '20px',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  minHeight: '465px',
  backgroundColor: TP_COLORS.white,
  border: '1px solid #73BFBC',
};

const titleTypoProps: TypographyProps = {
  color: TP_COLORS.tutopiyaGreenMain.darken,
  fontSize: '25px',
  fontWeight: '700',
};

const subTitleTypoProps: TypographyProps = {
  color: TP_COLORS.gray.darken,
  fontSize: '15px',
  maxWidth: '390px',
  textAlign: 'center',
};

const gridContainerProps: GridProps = {
  container: true,
  spacing: '20px',
  justifyContent: 'center',
  marginTop: '40px',
  marginBottom: '40px',
};

const cardAreaProps: CardActionAreaProps['sx'] = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-evenly',
  minWidth: '131px',
  minHeight: '147px',
  borderRadius: '20px',
};

const feedbackTypoProps: TypographyProps = {
  width: '99px',
  textAlign: 'center',
};

const buttonStyles: SxProps = {
  width: '250px',
  height: '49px',
  fontSize: '16px',
  fontWeight: '600',
};

const snackbarProps: SnackbarProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
  autoHideDuration: 5000,
  message:
    'Woah 🤩 You completed one step! Now it’s time to do some practice ✍🏻 Let’s keep the momentum going!',
  ContentProps: {
    sx: {
      fontSize: '15px',
      fontWeight: '600',
      width: 'max-content',
      borderRadius: '17px',
      background: 'linear-gradient(90deg, #01B8AF -34.18%, #00615D 100%)',
      boxShadow: '0px 0px 30px 10px #00376833',
    },
  },
};

export interface IFeedbackPage {
  pageWidth?: number;
  setActiveSection: (section: string) => void;
}

const FeedbackPage: React.FC<IFeedbackPage> = ({
  pageWidth,
  setActiveSection,
}) => {
  const dispatch = useDispatch();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const questionState = useAppSelector(getQuestionState);
  const { subTopic } = subTopicState;
  const questionList = questionState.list;

  // State
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const handleSubmit = () => {
    if (selectedValue && subTopic) {
      dispatch(
        AddResourceFeedback({
          feedback: selectedValue,
          subTopic: subTopic._id,
          subTopicName: subTopic.name,
        })
      );
    }
    setSelectedValue(null);

    if (questionList?.length) {
      setActiveSection('practice-questions');
      setAlertOpen(true);
    }
  };

  const feedbackOptions = [
    {
      value: 'bad',
      label: 'Hard to Understand',
      labelColor: TP_COLORS.orange.light,
      backgroundColor: '#FFF5F5',
    },
    {
      value: 'neutral',
      label: 'Could be Clearer',
      labelColor: TP_COLORS.yellow.light,
      backgroundColor: '#FEFFDC',
    },
    {
      value: 'good',
      label: 'Easy to Understand',
      labelColor: TP_COLORS.green.light,
      backgroundColor: '#E5F9E0',
    },
  ];

  return (
    <Stack sx={{ ...stackStyles, width: pageWidth }}>
      <Typography {...titleTypoProps}>How Well Did You Understand?</Typography>
      <Typography {...subTitleTypoProps}>
        Let us know how clearly this lesson was explained
      </Typography>
      <Grid {...gridContainerProps}>
        {feedbackOptions.map((item, index) => (
          <Grid item key={index}>
            <CardActionArea
              onClick={() => setSelectedValue(item.value)}
              sx={{
                ...cardAreaProps,
                border:
                  selectedValue === item.value
                    ? `1px solid ${item.labelColor}`
                    : '',
                backgroundColor: item.backgroundColor,
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/subtopicPage/${item.value}.png`}
                alt={item.value}
              />
              <Typography {...feedbackTypoProps} color={item.labelColor}>
                {item.label}
              </Typography>
            </CardActionArea>
          </Grid>
        ))}
      </Grid>

      <Button
        disableElevation
        variant="contained"
        onClick={handleSubmit}
        sx={{
          ...buttonStyles,
          visibility: selectedValue ? 'visible' : 'hidden',
        }}
      >
        {questionList?.length ? 'Test Your Skills! 🧠' : 'Submit Feedback'}
      </Button>
      <Snackbar
        {...snackbarProps}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </Stack>
  );
};

export default FeedbackPage;
